import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { ExitNudgeOption, ExitCouponConfig, UrgencyNudgeConfig, ExitCouponErrors } from 'src/components/ui-customization/sub-components/exit-coupon/ExitCouponNudgeExpandedSections'
import { useMerchant } from './MerchantContext'
import { useCheckoutUI } from './CheckoutUIContext'
import { useLoader } from './LoaderContext'
import { getDiscounts } from '../core/api-methods'
import { successToast, errorToast } from '../utils/toasters'
import { useLocale } from '../hooks/useLocal'

const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

interface ExitNudgeState {
  isEnabled: boolean
  selectedOption: ExitNudgeOption
  exitCoupon: ExitCouponConfig
  urgency: UrgencyNudgeConfig
}

export interface DiscoveredCouponData {
  coupon_id: string
  code: string
  title: string
  header: string
  concession_amount: number
  amount_type: string
}

interface ExitNudgeContextType {
  state: ExitNudgeState
  setIsEnabled: (v: boolean) => void
  setSelectedOption: (v: ExitNudgeOption) => void
  setExitCoupon: (v: ExitCouponConfig) => void
  setUrgency: (v: UrgencyNudgeConfig) => void
  showPreview: boolean
  setShowPreview: (v: boolean) => void
  isDirty: boolean
  saveExitNudge: () => Promise<void>
  discardExitNudge: () => void
  discoveredCouponData: DiscoveredCouponData | null
  exitCouponErrors: ExitCouponErrors
  setExitCouponErrors: (v: ExitCouponErrors) => void
  validateExitNudge: () => string | null
  couponExplicitlySelected: boolean
  setCouponExplicitlySelected: (v: boolean) => void
  exitCouponList: DiscoveredCouponData[]
  setExitCouponList: (v: DiscoveredCouponData[]) => void
  isCouponCreationOpen: boolean
  setIsCouponCreationOpen: (v: boolean) => void
}

const EXIT_ACTION_TO_OPTION: Record<string, ExitNudgeOption> = {
  survey: 'SURVEY',
  nudge: 'EXIT_CONFIRMATION',
  exit_coupon_nudge: 'EXIT_COUPON_NUDGE',
  urgency_nudge: 'URGENCY_NUDGE'
}

const OPTION_TO_EXIT_ACTION: Record<ExitNudgeOption, string> = {
  SURVEY: 'survey',
  EXIT_CONFIRMATION: 'nudge',
  EXIT_COUPON_NUDGE: 'exit_coupon_nudge',
  URGENCY_NUDGE: 'urgency_nudge'
}

const ExitNudgeContext = createContext<ExitNudgeContextType | undefined>(undefined)

export const ExitNudgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    getters: { merchant }
  } = useMerchant()
  const {
    getters: { checkoutUI },
    actions: { updateCheckoutUI }
  } = useCheckoutUI()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()

  const getInitialState = (): ExitNudgeState => {
    const exitAction = checkoutUI?.exitCheckoutAction ?? 'none'
    const savedNudge = checkoutUI?.exitNudge

    return {
      isEnabled: exitAction !== 'none',
      selectedOption: EXIT_ACTION_TO_OPTION[exitAction] ?? 'SURVEY',
      exitCoupon: {
        selectedCoupon: savedNudge?.exitCoupon?.selectedCoupon ?? '',
        couponValue: savedNudge?.exitCoupon?.couponValue ?? '',
        additionalSurvey: savedNudge?.exitCoupon?.additionalSurvey ?? false,
        badgeBackground: savedNudge?.exitCoupon?.badgeBackground ?? '#2C874A',
        badgeText: savedNudge?.exitCoupon?.badgeText ?? '#FFFFFF',
        badgeLabel: savedNudge?.exitCoupon?.badgeLabel ?? '',
        nudgeText: savedNudge?.exitCoupon?.nudgeText ?? '#27272A',
        nudgeMessage: savedNudge?.exitCoupon?.nudgeMessage ?? ''
      },
      urgency: {
        title: savedNudge?.urgency?.title ?? '',
        textColor: savedNudge?.urgency?.textColor ?? '#27272A',
        fomoMessage: savedNudge?.urgency?.fomoMessage ?? ''
      }
    }
  }

  const [state, setState] = useState<ExitNudgeState>(getInitialState)
  const [savedState, setSavedState] = useState<ExitNudgeState>(getInitialState)
  const [showPreview, setShowPreview] = useState(false)
  const [discoveredCouponData, setDiscoveredCouponData] = useState<DiscoveredCouponData | null>(null)
  const [exitCouponErrors, setExitCouponErrors] = useState<ExitCouponErrors>({})
  const [couponExplicitlySelected, setCouponExplicitlySelected] = useState(false)
  const [exitCouponList, setExitCouponList] = useState<DiscoveredCouponData[]>([])
  const [isCouponCreationOpen, setIsCouponCreationOpen] = useState(false)
  const fetchingCouponRef = useRef(false)

  // Re-sync when merchant data loads — skip if user has unsaved changes to avoid clobbering in-progress edits
  const isDirtyRef = useRef(false)
  useEffect(() => {
    if (isDirtyRef.current) return
    const initial = getInitialState()
    setState(initial)
    setSavedState(initial)
    setShowPreview(false)
  }, [
    checkoutUI?.exitCheckoutAction,
    checkoutUI?.exitNudge?.exitCoupon?.selectedCoupon,
    checkoutUI?.exitNudge?.exitCoupon?.couponValue,
    checkoutUI?.exitNudge?.exitCoupon?.additionalSurvey,
    checkoutUI?.exitNudge?.exitCoupon?.badgeBackground,
    checkoutUI?.exitNudge?.exitCoupon?.badgeText,
    checkoutUI?.exitNudge?.exitCoupon?.badgeLabel,
    checkoutUI?.exitNudge?.exitCoupon?.nudgeText,
    checkoutUI?.exitNudge?.exitCoupon?.nudgeMessage,
    checkoutUI?.exitNudge?.urgency?.title,
    checkoutUI?.exitNudge?.urgency?.textColor,
    checkoutUI?.exitNudge?.urgency?.fomoMessage,
    checkoutUI?.exitNudge?.selectedOption
  ])

  // Fetch discovered coupon whenever the selected coupon changes.
  // If the coupon no longer exists in the active list (e.g. deleted), clear it from both state and
  // savedState so the stale code doesn't show and the form doesn't appear dirty.
  useEffect(() => {
    const couponCode = state.exitCoupon.selectedCoupon
    const accountId = merchant?.accountId
    if (!couponCode || !accountId || fetchingCouponRef.current) return
    fetchingCouponRef.current = true
    getDiscounts(issuerId, accountId, 1, 1, 'discounts', 'CART_DISCOVERY', couponCode, undefined, 'EXIT_COUPON', '', 'ACTIVE')
      .then(res => {
        const found = res?.data?.data?.[0] ?? null
        setDiscoveredCouponData(found)
        if (!found) {
          setState(prev => ({ ...prev, exitCoupon: { ...prev.exitCoupon, selectedCoupon: '', couponValue: '' } }))
          setSavedState(prev => ({ ...prev, exitCoupon: { ...prev.exitCoupon, selectedCoupon: '', couponValue: '' } }))
          setCouponExplicitlySelected(false)
        }
      })
      .catch(() => setDiscoveredCouponData(null))
      .finally(() => { fetchingCouponRef.current = false })
  }, [state.exitCoupon.selectedCoupon, merchant?.accountId])

  const isDirty = JSON.stringify(state) !== JSON.stringify(savedState)
  isDirtyRef.current = isDirty

  const setIsEnabled = (v: boolean) => setState(prev => ({ ...prev, isEnabled: v }))
  const setSelectedOption = (v: ExitNudgeOption) => {
    if (v !== 'EXIT_COUPON_NUDGE') {
      setCouponExplicitlySelected(false)
      setState(prev => ({ ...prev, selectedOption: v, exitCoupon: { ...prev.exitCoupon, selectedCoupon: '' } }))
    } else {
      setState(prev => ({ ...prev, selectedOption: v }))
    }
  }
  const setExitCoupon = (v: ExitCouponConfig) => setState(prev => ({ ...prev, exitCoupon: v }))
  const setUrgency = (v: UrgencyNudgeConfig) => setState(prev => ({ ...prev, urgency: v }))

  const saveExitNudge = async () => {
    setLoaderProperties({ isLoaderVisible: true })

    const exitAction = state.isEnabled ? OPTION_TO_EXIT_ACTION[state.selectedOption] : 'none'

    const uiPayload: Record<string, unknown> = {
      exit_checkout_action: exitAction
    }

    if (state.selectedOption === 'EXIT_COUPON_NUDGE') {
      uiPayload.exit_coupon_nudge_config = {
        badge: {
          bg_color: state.exitCoupon.badgeBackground,
          text_color: state.exitCoupon.badgeText,
          html_text_string: state.exitCoupon.badgeLabel
        },
        nudge: {
          text_color: state.exitCoupon.nudgeText,
          html_text_string: state.exitCoupon.nudgeMessage
        },
        additional_survey_enabled: state.exitCoupon.additionalSurvey,
        selected_coupon: state.exitCoupon.selectedCoupon,
        coupon_value: state.exitCoupon.couponValue,
        cta_color: checkoutUI?.colorPalette?.primaryColor ?? '#18181B'
      }
    } else if (state.selectedOption === 'URGENCY_NUDGE') {
      uiPayload.urgency_nudge_config = {
        title: state.urgency.title,
        text_color: state.urgency.textColor,
        fomo_message: state.urgency.fomoMessage
      }
    }

    try {
      const response: any = await updateCheckoutUI(uiPayload)
      if (response?.error) {
        errorToast(response?.error)
      } else {
        setSavedState({ ...state })
        successToast('Changes Saved')
      }
    } catch (e) {
      errorToast('Error saving changes')
      console.error(e)
    }

    setLoaderProperties({ isLoaderVisible: false })
  }

  const discardExitNudge = () => {
    setState({ ...savedState })
  }

  const validateExitNudge = (): string | null => {
    if (!state.isEnabled || state.selectedOption !== 'EXIT_COUPON_NUDGE') return null
    if (!state.exitCoupon.selectedCoupon) return t('exit_nudge.error_choose_coupon')
    const fieldErrors: ExitCouponErrors = {}
    if (!state.exitCoupon.badgeLabel?.trim()) fieldErrors.badgeLabel = t('exit_nudge.error_badge_label_required')
    if (!state.exitCoupon.nudgeMessage?.trim()) fieldErrors.nudgeMessage = t('exit_nudge.error_nudge_message_required')
    if (Object.keys(fieldErrors).length > 0) {
      setExitCouponErrors(fieldErrors)
      return Object.values(fieldErrors)[0]
    }
    return null
  }

  return (
    <ExitNudgeContext.Provider
      value={{
        state,
        setIsEnabled,
        setSelectedOption,
        setExitCoupon,
        setUrgency,
        showPreview,
        setShowPreview,
        isDirty,
        saveExitNudge,
        discardExitNudge,
        discoveredCouponData,
        exitCouponErrors,
        setExitCouponErrors,
        validateExitNudge,
        couponExplicitlySelected,
        setCouponExplicitlySelected,
        exitCouponList,
        setExitCouponList,
        isCouponCreationOpen,
        setIsCouponCreationOpen
      }}
    >
      {children}
    </ExitNudgeContext.Provider>
  )
}

export const useExitNudge = () => {
  const ctx = useContext(ExitNudgeContext)
  if (!ctx) throw new Error('useExitNudge must be used within ExitNudgeProvider')
  return ctx
}
